import React from "react";
import splitArrayRole from "./utils/splitArrayRole";
import { SearchHit } from "../../../../creditSearch";

const setSplitAlignment = (text_alignment) => {
  switch (text_alignment) {
    case "left":
      return [" text-left ", " text-left "];
    case "justify":
      return [" text-left ", " text-right "];
    case "right":
      return [" text-right ", " text-right "];
    case "gutter":
      return [" text-right ", " text-left "];
    default:
      return [" text-right ", " text-left "];
  }
};

const getAlignment = (text_alignment, columns) => {
  switch (text_alignment) {
    case "left":
      return Array(columns).fill(" text-start ");
    case "justify":
      if (columns === 2) return [" text-start ", " text-end "];
      if (columns === 3) return [" text-start ", " text-center ", " text-end "];
      return [" text-start ", " text-end "];
    case "right":
      return Array(columns).fill(" text-end ");
    case "center":
      return Array(columns).fill(" text-center ");
    default:
      return Array(columns).fill(" text-end ");
  }
};

const getPadding = (textAlignment, noOfCols, colIdx, gutter) => {
  let pl = 0;
  let pr = 0;
  if (textAlignment === "gutter") {
    if (noOfCols === 2) {
      if (colIdx === 0) pr = gutter;
      if (colIdx === 1) pl = gutter;
    }
    if (noOfCols === 4) {
      if (colIdx === 1) pr = gutter;
      if (colIdx === 2) pl = gutter;
    }
  }
  return {
    paddingLeft: `${pl}px`,
    paddingRight: `${pr}px`,
  };
};

export default function RoleName2({ items, options, fontStyle, settings }) {
  // // Drop rows that have NEITHER a role nor a name, so empty rows don't render as
  // // blank gaps and don't count toward column balancing in the double-column
  // // (gutter/left/right/justify) and ragged layouts.
  // const nonEmptyItems = (items || []).filter((item) => {
  //   const hasRole = item?.role != null && String(item.role).trim() !== "";
  //   const hasName = item?.name != null && String(item.name).trim() !== "";
  //   return hasRole || hasName;
  // });

  const roleCount = items.filter((item) => item.role);

  const arraySplitDataByRole = options?.double_columns
    ? splitArrayRole(items, options).ans
    : [];

  // ==========================================================================================

  const arraySplitData = options?.double_columns
    ? []
    : splitArrayRole(items, options).ans;
  const textAlignment = getAlignment(options?.text_alignment, options?.columns);

  const { textDecoration: ___, ...roleStyle } = fontStyle.role;
  const { textDecoration: ____, ...nameStyle } = fontStyle.name;

  const marginBottom =
    Math.max(fontStyle.role.fontSize, fontStyle.name.fontSize) *
      fontStyle?.role?.lineHeight -
    Math.max(fontStyle.role.fontSize, fontStyle.name.fontSize) * 1.2;

  const trStyle = {
    display: "flex",
    justifyContent: "center",
    // Baseline-align role & name so they share the same bottom line even when
    // their font sizes differ (e.g. role 16px, name 10px). flex-end only aligns
    // the cell boxes, which drift apart once line-heights/sizes differ.
    alignItems: "baseline",
    // alignItems:  options.text_alignment === "left" ? "flex-start" : options.text_alignment === "right" ? "flex-end" : "baseline",
    // width: `${options.column_width}%`,
    width: "100%",
    margin: "0 auto",
    verticalAlign: "text-bottom",
    // marginBottom: `${marginBottom}px`,
    // lineHeight: fontStyle?.role?.lineHeight + 0.2,
    wordBreak: "break-word",
    paddingLeft: "10px",
    paddingRight: "10px",
    flexWrap: options.columns > 1 ? "wrap" : "nowrap",
    // minWidth: "200px", // Ensure a minimum width to avoid wrapping issues
  };

  // Row style for the side-by-side (gutter/left/right/justify) case. Uses a
  // real table row + table cells so role & name baseline-align via
  // vertical-align:baseline — which html2canvas reproduces in the render,
  // unlike flex `align-items: baseline`.
  // Role + name in one row via a table so they baseline-align through
  // vertical-align:baseline — which html2canvas reproduces in the export
  // (unlike flex align-items:baseline, which only aligns in the browser).
  const trStyleGutter = {
    display: "table",
    width: "100%",
    tableLayout: "fixed",
    margin: "0 auto",
    wordBreak: "break-word",
    paddingLeft: "10px",
    paddingRight: "10px",
    lineHeight: fontStyle?.role?.lineHeight + 0.2,
  };

  // Ragged row: same table/baseline approach, but the table shrinks to content
  // (auto width) and is centered, so role & name hug each other with the gutter.
  const trStyleRagged = {
    display: "table",
    margin: "0 auto",
    wordBreak: "break-word",
    paddingLeft: "10px",
    paddingRight: "10px",
    lineHeight: fontStyle?.role?.lineHeight + 0.2,
  };

  // ONE shared line-height for the whole role|name row. html2canvas mis-places
  // the baseline when the two cells have DIFFERENT line-heights (proven with an
  // html2canvas repro), so both cells must use the same value. Using the larger
  // of the two keeps enough room for the bigger text.
  // const rowLineHeight =
  //   Math.max(
  //     Number(fontStyle?.role?.lineHeight) || 1,
  //     Number(fontStyle?.name?.lineHeight) || 1
  //   ) + 0.2;

  // TUNABLE: pulls the ROLE text down toward the name's baseline. em-relative so
  // it scales with the render resolution. Increase to move role lower, use a
  // negative value to move it up, "0em" to disable.
  const ROLE_BASELINE_NUDGE = "0em";

  const tdStyleRight = {
    width: "50%",
    textAlign: "right",
    paddingRight: `${settings.gutter}px`,
    ...(options.name_on_left ? nameStyle : roleStyle),
    // table-cell + baseline: html2canvas reproduces vertical-align:baseline.
    display: "table-cell",
    verticalAlign: "baseline",
    fontKerning: "none",
    fontFeatureSettings: '"kern" 0',
    textRendering: "geometricPrecision",
    WebkitFontSmoothing: "antialiased",
    lineHeight: fontStyle?.lineHeight,
  };

  const tdStyleLeft = {
    width: "50%",
    textAlign: "left",
    paddingLeft: `${settings.gutter}px`,
    ...(options.name_on_left ? roleStyle : nameStyle),
    display: "table-cell",
    verticalAlign: "baseline",
    fontKerning: "none",
    fontFeatureSettings: '"kern" 0',
    textRendering: "geometricPrecision",
    WebkitFontSmoothing: "antialiased",
    lineHeight: fontStyle?.lineHeight,
  };

  const tdStyleRightColumn = {
    textAlign: "right",
    paddingRight: `${settings?.ragged_gutter / 2}px`,
    ...(options.name_on_left ? nameStyle : roleStyle),
    display: "table-cell",
    verticalAlign: "baseline",
    fontKerning: "none",
    fontFeatureSettings: '"kern" 0',
    textRendering: "geometricPrecision",
    WebkitFontSmoothing: "antialiased",
    lineHeight: fontStyle?.lineHeight,
  };

  const tdStyleLeftColumn = {
    textAlign: "left",
    paddingLeft: `${settings.ragged_gutter / 2}px`,
    ...(options.name_on_left ? roleStyle : nameStyle),
    display: "table-cell",
    verticalAlign: "baseline",
    fontKerning: "none",
    fontFeatureSettings: '"kern" 0',
    textRendering: "geometricPrecision",
    WebkitFontSmoothing: "antialiased",
  lineHeight: fontStyle?.lineHeight,
  };

  return options.double_columns ? (
    options.text_alignment === "ragged" ? (
      // Double Column (Ragged Align)

      <div
        className={`flex flex-row flex-wrap ${
          options?.columns === 1 ? "justify-center" : ""
        } 
        ${options?.text_alignment === "gutter" ? " justify-center " : ""}`}
        style={{
          width: `${options.column_width * options.columns}%`,
          margin: "0 auto",
          wordBreak: "break-word",
        }}
      >
        {arraySplitDataByRole?.map((items, index) => (
          <div
            key={index}
            className={`flex flex-col flex-1 break-words float-left`}
            style={{ width: `${options.column_width}%` }}
          >
            {items?.map((item, index) => {
              return (
                <div key={index} className="roleName_tr" style={trStyleRagged}>
                  <div
                    className="roleName_td FontSIzeScale"
                    style={tdStyleRightColumn}
                  >
                    <span
                      style={{
                        textDecoration: options.name_on_left
                          ? fontStyle.name.textDecoration
                          : item.role == null
                          ? "none"
                          : fontStyle.role.textDecoration,
                        color: options.name_on_left
                          ? ""
                          : item.role == null
                          ? "transparent"
                          : fontStyle.role.color,
                      }}
                    >
                      {options.name_on_left
                        ? <SearchHit text={item.name} />
                        : item.role == null
                        ? ". "
                        : <SearchHit text={item.role} />}
                    </span>
                  </div>
                  <div
                    className="roleName_td FontSIzeScale"
                    style={tdStyleLeftColumn}
                  >
                    <span
                      style={{
                        textDecoration: options.name_on_left
                          ? item.role == null
                            ? "none"
                            : fontStyle.role.textDecoration
                          : fontStyle.name.textDecoration,
                        color: options.name_on_left
                          ? item.role == null
                            ? "transparent"
                            : fontStyle.role.color
                          : "",
                      }}
                    >
                      {options.name_on_left
                        ? item.role == null
                          ? " ."
                          : <SearchHit text={item.role} />
                        : <SearchHit text={item.name} />}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    ) : (
      // Double Column (Gutter Align/Left/Right/Justify Align)

      <div
        className={`flex flex-row flex-wrap ${
          options?.columns === 1 ? "justify-center" : ""
        } 
        ${options?.text_alignment === "gutter" ? " justify-center " : ""}`}
        style={{
          width: `${options.column_width * options.columns}%`,
          margin: "0 auto",
          wordBreak: "break-word",
        }}
      >
        {arraySplitDataByRole?.map((items, index) => (
          <div
            key={index}
            className={`flex flex-col flex-1 break-words float-left`}
            style={{ width: `${options.column_width}%` }}
          >
            {items.map((item, index) => {
              return (
                <div key={index} className="roleName_tr" style={trStyleGutter}>
                  <div
                    style={tdStyleRight}
                    className="roleName_td FontSIzeScale"
                  >
                    <span
                      className={`inline-block w-full span1 ${
                        setSplitAlignment(options.text_alignment)[0]
                      }`}
                      style={{
                        textDecoration: options.name_on_left
                          ? fontStyle.name.textDecoration
                          : item.role == null
                          ? "none"
                          : fontStyle.role.textDecoration,
                        color: options.name_on_left
                          ? ""
                          : item.role == null
                          ? "transparent"
                          : fontStyle.role.color,
                        fontFamily: options.name_on_left
                          ? fontStyle.name.fontFamily
                          : fontStyle.role.fontFamily,
                        // This span holds the ROLE when name is NOT on the left.
                        transform: options.name_on_left
                          ? undefined
                          : `translateY(${ROLE_BASELINE_NUDGE})`,
                        // backgroundColor:'gray'
                      }}
                    >
                      {options.name_on_left
                        ? <SearchHit text={item.name} />
                        : item.role == null
                        ? "No Roll"
                        : <SearchHit text={item.role} />}
                    </span>
                  </div>
                  <div
                    style={tdStyleLeft}
                    className="roleName_td FontSIzeScale"
                  >
                    <span
                      className={`inline-block w-full span2 ${
                        setSplitAlignment(options.text_alignment)[1]
                      }`}
                      style={{
                        textDecoration: options.name_on_left
                          ? item.role == null
                            ? "none"
                            : fontStyle.role.textDecoration
                          : fontStyle.name.textDecoration,
                        color: options.name_on_left
                          ? item.role == null
                            ? "transparent"
                            : fontStyle.role.color
                          : "",
                        fontFamily: options.name_on_left
                          ? fontStyle.role.fontFamily
                          : fontStyle.name.fontFamily,
                        // This span holds the ROLE when name IS on the left.
                        transform: options.name_on_left
                          ? `translateY(${ROLE_BASELINE_NUDGE})`
                          : undefined,
                      }}
                    >
                      {options.name_on_left
                        ? item.role == null
                          ? "No Roll"
                          : <SearchHit text={item.role} />
                        : <SearchHit text={item.name} />}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    )
  ) : (
    // Single Column (Gutter Align)

    <div
      className={`flex flex-row flex-wrap ${
        options?.columns === 1 ? "justify-center" : ""
      } 
      ${options?.text_alignment === "gutter" ? " justify-center " : ""}`}
      style={{
        width: `${options.column_width * options.columns}%`,
        margin: "0 auto",
        wordBreak: "break-word",
        justifyContent:
          options.text_alignment === "left"
            ? "flex-start"
            : options.text_alignment === "right"
            ? "flex-end"
            : "center",
      }}
    >
      {arraySplitData?.map((items, index) => (
        <div
          key={index}
          className={`flex flex-col flex-1 break-words float-left`}
        >
          {items.map((item, index) => (
            <div
              key={index}
              className={`flex flex-col`}
              style={{
                alignItems:
                  options.text_alignment === "left"
                    ? "flex-start"
                    : options.text_alignment === "right"
                    ? "flex-end"
                    : "center",
              }}
            >
              <span
                className={
                  options.name_on_left
                    ? " order-1 FontSIzeScale"
                    : " order-0 FontSIzeScale"
                }
                style={{
                  ...roleStyle,
                  textDecoration:
                    item.role == null ? "none" : fontStyle.role.textDecoration,
                  color:
                    item.role == null ? "transparent" : fontStyle.role.color,
                  lineHeight:
                    item.role == null
                      ? "0"
                      : fontStyle?.role?.lineHeight + 0.12,
                  paddingLeft: "1px",
                  paddingRight: "1px",
                }}
              >
                {item.role == null ? "No Roll" : <SearchHit text={item.role} />}
              </span>
              <span
                className={
                  options.name_on_left
                    ? " order-0 FontSIzeScale"
                    : " order-1 FontSIzeScale"
                }
                style={{
                  ...nameStyle,
                  textDecoration: fontStyle.name.textDecoration,
                  color:
                    item.name == null ? "transparent" : fontStyle.name.color,
                  lineHeight:
                    item.name == null
                      ? "0"
                      : fontStyle?.name?.lineHeight + 0.12,
                  paddingLeft: "1px",
                  paddingRight: "1px",
                }}
              >
                {item.name == null ? "No Roll" : <SearchHit text={item.name} />}
              </span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}